// src/components/AppGuide.tsx
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle } from 'react-feather';
import './rag-chat-shell.css';

// 可重用的引导按钮组件
interface GuideButtonProps {
  label: string;
  message: string;
  onSendMessage: (text: string) => void;
}

const GuideButton: React.FC<GuideButtonProps> = ({ label, message, onSendMessage }) => {
  return (
    <button
      type="button"
      className="app-guide-prompt-button"
      onClick={() => onSendMessage(message)}
    >
      {label}
    </button>
  );
};

// 按钮配置定义
interface ButtonConfig {
  label: string;
  message: string;
}

// 创建 Context
interface AppGuideContextType {
  completeRegistration: () => void;
  startGuide: () => void;
  fillDemoData?: () => void;
  registerFillDemoData?: (callback: () => void) => void;
  // 新增：NX集成助手引导
  startNXAssistantGuide?: () => void;
}

const AppGuideContext = createContext<AppGuideContextType | null>(null);

// 自定义 Hook
export const useAppGuide = () => {
  const context = useContext(AppGuideContext);
  return context;
};

export const AppGuideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  
  // 根据模式获取按钮配置（从翻译文件中读取）
  const getButtonConfigByMode = (chatMode: string): ButtonConfig[] => {
    const normalizedMode = chatMode === 'normal' ? 'regular' : chatMode;
    const exampleButtons = t(`guide.nxAssistant.exampleButtons.${normalizedMode}`, { returnObjects: true });
    
    // 确保返回的是数组格式
    if (Array.isArray(exampleButtons)) {
      return exampleButtons as ButtonConfig[];
    }
    
    // 如果翻译返回的不是数组，返回空数组作为后备
    return [];
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [run, setRun] = useState(false);
  const [isGuideClosing, setIsGuideClosing] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [showHelpButton, setShowHelpButton] = useState(true);
  const [fillDemoDataCallback, setFillDemoDataCallback] = useState<(() => void) | null>(null);
  // 新增：NX助手引导状态
  const [guideType, setGuideType] = useState<'login' | 'nx-assistant'>('login');
  // 新增：快速引导模式状态
  const [isQuickGuide, setIsQuickGuide] = useState<boolean>(false);

  // 添加demo登录功能
  const handleDemoLogin = useCallback(async () => {
    const demoLoginEvent = new CustomEvent('demoLogin', {
      detail: { username: 'demo', password: '123456' }
    });
    window.dispatchEvent(demoLoginEvent);
    setRun(false);
    localStorage.setItem('appGuideCompleted', 'true');
  }, []);

  // 根据引导类型返回不同的步骤
  const getSteps = (): Step[] => {
    if (guideType === 'nx-assistant') {
      if (isQuickGuide) {
        return getQuickGuideSteps();
      }
      return getNXAssistantSteps();
    }
    return getLoginSteps();
  };

  // 登录引导步骤：5步引导：登录页2步 + 注册页1步 + 返回登录页2步
  const getLoginSteps = (): Step[] => [
    // 第1步: 欢迎
    {
      target: 'body',
      content: (
        <div>
          <strong>{t('guide.welcome.title')}</strong>
          <div style={{ marginBottom: '20px' }}>{t('guide.welcome.description')}</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              type="button"
              className="industrial-button"
              onClick={handleDemoLogin}
              style={{ minWidth: 160 }}
            >
              {t('guide.welcome.demoButton')}
            </button>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    // 第2步: 高亮注册按钮
    {
      target: '#register-link',
      content: (
        <div>
          <strong>{t('guide.register.title')}</strong>
          <div>{t('guide.register.description')}</div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    // 第3步: 注册页填写提醒
    {
      target: '.register-form',
      content: (
        <div>
          <strong>{t('guide.fillForm.title')}</strong>
          <div>{t('guide.fillForm.description')}</div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    // 第4步: 等待审核
    {
      target: 'body',
      content: (
        <div>
          <strong>{t('guide.waitApproval.title')}</strong>
          <div>{t('guide.waitApproval.description')}</div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    // 第5步: 寻求帮助
    {
      target: '#help-button',
      content: (
        <div>
          <strong>{t('guide.needHelp.title')}</strong>
          <div>{t('guide.needHelp.description')}</div>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
  ];

  // NX集成助手引导步骤
  const getNXAssistantSteps = (): Step[] => {
    const currentChatMode = 'nx';
    const normalizedMode = 'nx';
    const buttonConfigs = getButtonConfigByMode(currentChatMode);
    
    return [
      // 第1步: 欢迎使用助手（根据模式显示不同标题）
      {
        target: 'body',
        content: (
          <div>
            <strong>🚀 {normalizedMode === 'nx' 
              ? t('guide.nxAssistant.welcome.title')
              : t('guide.nxAssistant.welcome.titleRegular')
            }</strong>
            <div style={{ marginTop: '12px', lineHeight: '1.6' }}>
              {currentChatMode === 'nx' 
                ? (
                  <>
                    <div>{t('guide.nxAssistant.welcome.descriptionNX', 'NX集成助手是您的CAD开发助手，可以帮您生成NX代码、解答技术问题，让您的工作更高效！')}</div>
                    <div style={{ marginTop: '10px', whiteSpace: 'pre-wrap', textAlign: 'left' }}>{t('chatGuideNx', "Setup NX Help.")}</div>
                  </>
                )
                : t('guide.nxAssistant.welcome.descriptionRegular', '智能助手可以帮您回答各种工程技术问题，提供专业的解决方案和建议！')
              }
            </div>
          </div>
        ),
        placement: 'center' as const,
        disableBeacon: true,
      },
      
      // 第2步: Ask NX 按钮（仅NX模式显示）
      ...(normalizedMode === 'nx' ? [{
        target: '[data-testid="ask-nx-button"], .ask-nx-button',
        content: (
          <div>
            <strong>🔧 {t('guide.nxAssistant.askNX.title')}</strong>
            <div style={{ marginTop: '8px' }}>
              {t('guide.nxAssistant.askNX.description')}
            </div>
          </div>
        ),
        placement: 'bottom' as const,
        disableBeacon: true,
      }] : []),
      
      // 聊天输入框 - 最后一步，使用动态按钮
      {
        target: '[data-testid="nx-chat-input"], .nx-chat-input, textarea, .MuiTextField-root textarea, .MuiInputBase-input, .MuiTextField-root:last-child',
        content: (
          <div>
            <strong>💬 {t('guide.nxAssistant.input.title')}</strong>
            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              {normalizedMode === 'nx' 
                ? t('guide.nxAssistant.input.descriptionNX')
                : t('guide.nxAssistant.input.descriptionRegular')
              }
            </div>
            {/* 添加斜杠命令说明 */}
            <div style={{ 
              marginBottom: '12px', 
              padding: '8px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              fontSize: '1.083rem'
            }}>
              <strong>💡 {t('guide.nxAssistant.slashCommands.title')}</strong>
              <div style={{ marginTop: '4px' }}>
                {t('guide.nxAssistant.slashCommands.description')}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {buttonConfigs.map((config: ButtonConfig, index) => (
                <GuideButton
                  key={index}
                  label={config.label}
                  message={config.message}
                  onSendMessage={fillChatInputAndFinish}
                />
              ))}
            </div>
          </div>
        ),
        placement: 'top' as const,
        disableBeacon: true,
      },
    ];
  };

  // 快速引导步骤 - 仅显示按钮菜单
  const getQuickGuideSteps = (): Step[] => {
    const currentChatMode = 'nx';
    const normalizedMode = 'nx';
    const buttonConfigs = getButtonConfigByMode(currentChatMode);
    
    return [
      {
        target: '[data-testid="nx-chat-input"], .nx-chat-input, textarea, .MuiTextField-root textarea, .MuiInputBase-input, .MuiTextField-root:last-child',
        content: (
          <div>
            <strong>💡 {t(`guide.nxAssistant.exampleTitle.${normalizedMode}`)}</strong>
            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              {t('guide.nxAssistant.exampleDescription')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {buttonConfigs.map((config: ButtonConfig, index) => (
                <GuideButton
                  key={index}
                  label={config.label}
                  message={config.message}
                  onSendMessage={(text) => {
                    console.log('🚀 [AppGuide Quick] 准备发送消息:', text);
                    
                    const realCurrentMode = 'nx';
                    console.log('[AppGuide Quick] ✅ 实际当前模式:', realCurrentMode);
                    
                    // 立即关闭快速引导
                    setRun(false);
                    setIsQuickGuide(false);
                    
                    // 发送消息事件，包含准确的模式信息
                    const sendTestingEvent = new CustomEvent('sendTestingMessage', {
                      detail: { 
                        text: text,
                        mode: realCurrentMode,
                        timestamp: Date.now()
                      }
                    });
                    window.dispatchEvent(sendTestingEvent);
                    
                    console.log('[AppGuide Quick] 🎯 已发送消息事件，模式:', realCurrentMode);
                  }}
                />
              ))}
            </div>
            <div style={{ marginTop: '12px', fontSize: '1rem', color: '#999', textAlign: 'center' }}>
              {t('guide.nxAssistant.quickGuideHint')}
            </div>
          </div>
        ),
        placement: 'top' as const,
        disableBeacon: true,
      }
    ];
  };

  // 回调处理
  const handleCallback = (data: CallBackProps) => {
    const { action, index, type, status } = data;
    
    console.log('🎯 [AppGuide] Joyride callback:', { action, index, type, status, stepIndex });

    // 当用户点击 "x" 关闭按钮时，action 为 'close'
    if (action === 'close') {
      console.log('✅ [AppGuide] 用户点击了关闭按钮，停止引导');
      setRun(false);
      setIsQuickGuide(false); // 重置快速引导状态
      
      // 根据引导类型设置完成状态
      if (guideType === 'nx-assistant') {
        localStorage.setItem('nxAssistantGuideCompleted', 'true');
      } else {
        localStorage.setItem('appGuideCompleted', 'true');
      }
      
      setShowHelpButton(true);
      return; // 提前返回，不再执行后续逻辑
    }

    // 仅在引导完成或被跳过时设置完成状态 (包括关闭按钮)
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        console.log('✅ [AppGuide] 用户已完成或跳过引导');
        setRun(false);
        setIsQuickGuide(false); // 重置快速引导状态
        
        if (guideType === 'nx-assistant') {
          localStorage.setItem('nxAssistantGuideCompleted', 'true');
        } else {
        localStorage.setItem('appGuideCompleted', 'true');
        }
        
        setShowHelpButton(true);
        return;
      }
    
    // 只处理用户主动点击的 next 操作，类型必须是 step:after
    if (action === 'next' && type === 'step:after') {
      
      if (guideType === 'nx-assistant') {
        // NX助手引导的 next 处理
        console.log(`NX助手引导 - 第${index + 1}步完成`);
        
        if (index < 4) {
          // 前4步都是简单的下一步
          setStepIndex(index + 1);
        } else {
          // 最后一步（聊天输入框），结束引导
          setRun(false);
          localStorage.setItem('nxAssistantGuideCompleted', 'true');
          setShowHelpButton(true);
        }
        return;
      }
      
      // 登录引导的 next 处理
      // 第1步点击"下一步"，进入第2步
      if (index === 0 && stepIndex === 0) {
        console.log('第1步完成，进入第2步');
        setStepIndex(1);
        return;
      }
      
      // 第2步点击"去注册"，跳转到注册页
      if (index === 1 && stepIndex === 1) {
        console.log('第2步点击去注册，跳转到注册页');
        setRun(false);
        navigate('/register');
        // 延迟设置第3步，确保页面跳转完成
        setTimeout(() => {
          setStepIndex(2);
          setRun(true);
        }, 500);
        return;
      }
      
      // 第3步点击"立即填写"，预填示例数据
      if (index === 2 && stepIndex === 2) {
        console.log('第3步完成，预填示例数据');
        // 调用预填示例数据的回调函数
        if (fillDemoDataCallback) {
          fillDemoDataCallback();
        }
        setRun(false);
        // 不设置完成状态，等待注册成功回调
        return;
      }
      
      // 第4步点击"下一步"，进入第5步
      if (index === 3 && stepIndex === 3) {
        console.log('第4步完成，进入第5步');
        setStepIndex(4);
        return;
      }
      
      // 第5步点击"完成"，结束引导
      if (index === 4 && stepIndex === 4) {
        console.log('第5步完成，结束引导');
        setRun(false);
        localStorage.setItem('appGuideCompleted', 'true');
        setShowHelpButton(true);
        return;
      }
    }
    
    // 上一步
    if (action === 'prev' && type === 'step:after') {
      console.log('上一步操作:', { index, stepIndex, guideType });
      
      if (guideType === 'nx-assistant') {
        // NX助手引导的 prev 处理 - 可以自由返回上一步
        if (stepIndex > 0) {
          setStepIndex(stepIndex - 1);
        }
      } else {
        // 登录引导的 prev 处理 - 第3步（stepIndex=2）和第4步（stepIndex=3）不允许返回
      if (stepIndex > 0 && stepIndex !== 2 && stepIndex !== 3) {
        setStepIndex(stepIndex - 1);
      }
    }
    }
    
  };

  // 手动开始引导
  const startGuide = useCallback(() => {
    console.log('开始登录页面引导');
    setIsGuideClosing(false);  // 重置关闭标志
    setGuideType('login');  // 确保是登录引导类型
    
    // 根据当前页面决定开始步骤
    if (location.pathname === '/register') {
      // 在注册页面，直接从第3步开始（预填提醒）
      setStepIndex(2);
    } else {
      // 在登录页面，从第1步开始
      setStepIndex(0);
    }
    
    setRun(true);
    setShowHelpButton(false);
    localStorage.removeItem('appGuideCompleted');
    localStorage.removeItem('showStep4'); // 清除可能存在的步骤4标记
  }, [location.pathname]);

  // 注册成功后调用
  const completeRegistration = useCallback(() => {
    // 设置标记，等待用户跳转到登录页后显示第4步
    localStorage.setItem('showStep4', 'true');
    setStepIndex(3); // 第4步索引是3
    setRun(false); // 先停止引导
  }, []);

  // 页面加载时的逻辑
  useEffect(() => {
    const isCompleted = localStorage.getItem('appGuideCompleted');
    const showStep4 = localStorage.getItem('showStep4');
    
    // 当访问登录或注册页面时，确保引导类型是login并重置状态
    if (location.pathname === '/login' || location.pathname === '/register') {
      if (guideType !== 'login') {
        setGuideType('login');
        setRun(false);  // 停止可能正在运行的其他引导
        setStepIndex(0);  // 重置步骤
      }
    }
    
    if (location.pathname === '/login' && showStep4 === 'true') {
      // 注册成功返回登录页，显示第4步
      console.log('检测到showStep4标记，显示第4步等待审核');
      localStorage.removeItem('showStep4');
      setGuideType('login');  // 确保使用登录引导
      setTimeout(() => {
        setStepIndex(3);
        setRun(true);
        setShowHelpButton(false);
      }, 1000);
    } else if (!run && !isCompleted) {
      // 显示帮助按钮
      setShowHelpButton(true);
    }
  }, [location.pathname, run, startGuide, stepIndex, guideType]); // 添加guideType依赖

  // 注册预填数据的方法
  const registerFillDemoData = useCallback((callback: () => void) => {
    setFillDemoDataCallback(() => callback);
  }, []);

  // 填充聊天输入框的方法 - React方式
  const fillChatInput = useCallback((text: string) => {
    console.log('填充文本:', text);
    
    // 使用自定义事件来通知RagChat组件填充输入框
    const fillInputEvent = new CustomEvent('fillChatInput', {
      detail: { text }
    });
    window.dispatchEvent(fillInputEvent);
    
    console.log('已发送fillChatInput事件:', text);
  }, []);

  // 发送测试消息并结束引导
  const fillChatInputAndFinish = useCallback((text: string) => {
    console.log('🚀 [AppGuide] 发送测试消息并结束引导:', text);
    
    const realCurrentMode = 'nx';
    console.log('[AppGuide] ✅ 实际当前模式:', realCurrentMode);
    
    // 使用自定义事件来通知RagChat组件直接发送消息
    const sendTestingEvent = new CustomEvent('sendTestingMessage', {
      detail: { 
        text: text,
        mode: realCurrentMode,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(sendTestingEvent);
    
    console.log('[AppGuide] 🎯 已发送sendTestingMessage事件，模式:', realCurrentMode);
    
    // 延迟结束引导 - 根据引导类型设置不同的完成状态
    setTimeout(() => {
      console.log('[AppGuide] ✅ 测试消息发送完成，结束助手引导');
      setRun(false);
      setIsQuickGuide(false); // 重置快速引导状态
      
      if (guideType === 'nx-assistant') {
        localStorage.setItem('nxAssistantGuideCompleted', 'true');
      } else {
        localStorage.setItem('appGuideCompleted', 'true');
      }
      
      setShowHelpButton(true);
    }, 1500);
  }, [guideType]);

  // 检测输入框是否处于焦点状态（真正的焦点检测）
  const isInputFieldFocused = useCallback((): boolean => {
    console.log('🔍 [AppGuide] 检测输入框焦点状态...');
    
    const inputSelectors = [
      '[data-testid="nx-chat-input"] textarea',
      '[data-testid="nx-chat-input"] input', 
      '.nx-chat-input textarea',
      '.nx-chat-input input',
      '.MuiTextField-root textarea',
      '.MuiInputBase-input'
    ];
    
    // 只检查焦点状态，不检查内容
    for (const selector of inputSelectors) {
      const element = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
      if (element && element === document.activeElement) {
        console.log('✅ [AppGuide] 输入框当前有焦点 (激活状态):', selector);
        return true;
      }
    }
    
    console.log('❌ [AppGuide] 输入框未激活');
    return false;
  }, []);

  // 显示快速引导按钮菜单（仅按钮，无完整引导流程）
  const showQuickGuide = useCallback(() => {
    console.log('显示快速引导按钮菜单');
    
    // 设置为快速引导模式
    setIsQuickGuide(true);
    setGuideType('nx-assistant');
    setStepIndex(0);
    setRun(true);
    setShowHelpButton(false);
    
    console.log('快速引导已启动');
  }, []);

  // 启动NX集成助手引导 - 修改为支持快速引导检测
  const startNXAssistantGuide = useCallback(() => {
    console.log('🚀 [AppGuide] 启动NX集成助手引导');
    
    // 改进的用户活动检测：检查是否有输入内容或最近的用户活动
    const hasUserActivity = isInputFieldFocused();
    
    if (hasUserActivity) {
      console.log('✅ [AppGuide] 检测到用户活动，启动快速引导模式');
      showQuickGuide();
      return;
    }
    
    console.log('📚 [AppGuide] 未检测到用户活动，启动完整引导流程');
    setIsGuideClosing(false);
    setIsQuickGuide(false); // 确保是完整引导模式
    setGuideType('nx-assistant');
    setStepIndex(0);
    setRun(true);
    setShowHelpButton(false);
    localStorage.removeItem('nxAssistantGuideCompleted');
  }, [isInputFieldFocused, showQuickGuide]);

  return (
    <AppGuideContext.Provider value={{ 
      completeRegistration, 
      startGuide, 
      fillDemoData: fillDemoDataCallback || undefined,
      registerFillDemoData,
      startNXAssistantGuide
    }}>
      {children}
      
      {/* 右下角帮助按钮已移除 - 现在使用 AppBar 中的问号图标 */}
      
      {/* Joyride 组件 - 根据页面和引导类型显示 */}
      {(((location.pathname === '/login' || location.pathname === '/register') && guideType === 'login') || 
        (location.pathname === '/dashboard' && guideType === 'nx-assistant')) && (
                  <Joyride
            steps={getSteps()}
            run={run}
            stepIndex={stepIndex}
            continuous={true}
            showProgress={false}
            showSkipButton={true}

            callback={handleCallback}
            locale={{
              back: t('guide.buttons.back'),
              close: t('guide.buttons.close'),
              last: t('guide.buttons.complete'),
              next: (() => {
                if (guideType === 'nx-assistant') {
                  // NX助手引导按钮
                  switch(stepIndex) {
                    case 0: return t('guide.buttons.next');
                    case 1: return t('guide.buttons.next');
                    case 2: return t('guide.buttons.next');
                    case 3: return t('guide.buttons.next');
                    case 4: return t('guide.buttons.complete');
                    default: return t('guide.buttons.next');
                  }
                } else {
                  // 登录引导按钮
                switch(stepIndex) {
                  case 0: return t('guide.buttons.next'); // 第1步：下一步
                  case 1: return t('guide.buttons.goRegister'); // 第2步：去注册
                  case 2: return t('guide.buttons.goFill'); // 第3步：去填写
                  case 3: return t('guide.buttons.next'); // 第4步：下一步
                  case 4: return t('guide.buttons.complete'); // 第5步：完成
                  default: return t('guide.buttons.next');
                  }
                }
              })(),
              skip: t('guide.buttons.skip'),
            }}
          styles={{
            options: {
              zIndex: 2001,
              primaryColor: '#1976d2',
              backgroundColor: '#fff',
            },
            buttonBack: (() => {
              if (guideType === 'nx-assistant') {
                // NX助手引导 - 所有步骤都可以返回
                return {};
              } else {
                // 登录引导 - 特定步骤隐藏返回按钮
                return (stepIndex === 2 || stepIndex === 3) ? { display: 'none' } : {};
              }
            })(),
          }}
        />
      )}
    </AppGuideContext.Provider>
  );
};

// 导出帮助按钮组件，用于在 AppBar 中显示
export const AppGuideHelpButton: React.FC = () => {
  const { t } = useTranslation();
  const context = useContext(AppGuideContext);

  if (!context) {
    return null;
  }

  const { startGuide } = context;

  return (
    <button
      type="button"
      className="app-guide-help-button"
      onClick={startGuide}
      title={t('guide.helpButton')}
      aria-label={t('guide.helpButton')}
    >
      <HelpCircle size={18} aria-hidden="true" />
    </button>
  );
};

// 导出默认组件（为了兼容）
const AppGuide: React.FC = () => null;
export default AppGuide;
